import axios from 'axios';
import toast from 'react-hot-toast';
import { fetchAuthSession } from 'aws-amplify/auth';
import { withTimeout, AUTH_TIMEOUT_MS } from './network';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://ufvz2izpyl.execute-api.eu-west-2.amazonaws.com/powderlog';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

async function getIdToken() {
  try {
    const session = await withTimeout(fetchAuthSession(), AUTH_TIMEOUT_MS, 'fetchAuthSession');
    return session?.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

api.interceptors.request.use(async (config) => {
  const token = await getIdToken();
  if (token) config.headers.Authorization = token;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // 401s are not auto-redirected — anonymous users will see 401s on
    // authenticated endpoints and the caller decides what to do (typically
    // fall back to the local Dexie repo).
    if (status === 401) {
      return Promise.reject(error);
    }
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'Something went wrong';
    toast.error(message);
    return Promise.reject(error);
  },
);

export const apiGet = (path, params) => api.get(path, { params }).then((r) => r.data);
export const apiPost = (path, body) => api.post(path, body).then((r) => r.data);
export const apiPut = (path, body) => api.put(path, body).then((r) => r.data);
export const apiDelete = (path) => api.delete(path).then((r) => r.data);

export async function apiGetRaw(path, queryParams = {}) {
  const token = await getIdToken();
  const qs = new URLSearchParams(queryParams).toString();
  const url = `${BASE_URL}${path}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: token ? { Authorization: token } : {} });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  return res;
}

export const syncSession = (sessionData) => apiPost('/sessions/sync', sessionData);
export const getSessions = (params) => apiGet('/sessions', params);
export const getSession = (sessionId) => apiGet(`/sessions/${sessionId}`);
export const getSessionTrack = (sessionId) => apiGet(`/sessions/${sessionId}/track`);
export const updateSession = (sessionId, data) => apiPut(`/sessions/${sessionId}`, data);
export const deleteSession = (sessionId) => apiDelete(`/sessions/${sessionId}`);

export const getSeasonStats = (season) => apiGet(`/stats/season/${season}`);
export const getLifetimeStats = () => apiGet('/stats/lifetime');
export const getPersonalRecords = () => apiGet('/stats/records');

export const addFriend = (data) => apiPost('/friends/add', data);
export const getFriends = () => apiGet('/friends');
export const acceptFriend = (userId) => apiPost(`/friends/accept/${userId}`);
export const removeFriend = (userId) => apiDelete(`/friends/${userId}`);

// Silent — both location calls run as passive background operations (30s
// broadcast / 15s poll). Failures (429 rate limit, 403 not-premium, network
// blips) must never toast; callers inspect err.status instead.
export const updateLocation = async (data) => {
  const token = await getIdToken();
  const res = await fetch(`${BASE_URL}/location`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
    body: JSON.stringify(data),
  });
  if (!res.ok) return null;
  return res.json();
};
export const getFriendLocations = async () => {
  const token = await getIdToken();
  const res = await fetch(`${BASE_URL}/location/friends`, {
    headers: token ? { Authorization: token } : {},
  });
  if (!res.ok) {
    const err = new Error(`friend locations fetch failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};
export const getLeaderboard = (params) => apiGet('/leaderboard', params);

export const searchResorts = ({ query, country, limit } = {}) =>
  apiGet('/resorts/search', { q: query, country, limit });
export const getResort = (resortId) => apiGet(`/resorts/${resortId}`);
export const getResortConditions = (resortId) => apiGet(`/resorts/${resortId}/conditions`);
// Silent — no error toast. Run data is optional/bundled; a 404 here is a
// normal state, not an error the user needs to see.
export const getResortRuns = async (resortId) => {
  const token = await getIdToken();
  const res = await fetch(`${BASE_URL}/resorts/${resortId}/runs`, {
    headers: token ? { Authorization: token } : {},
  });
  if (!res.ok) throw new Error(`runs fetch failed (${res.status})`);
  return res.json();
};
export const getResortTrailMap = (resortId) => apiGet(`/resorts/${resortId}/map`);
export const getNearbyResorts = ({ latitude, longitude, radiusKm } = {}) =>
  apiGet('/resorts/nearby', { lat: latitude, lng: longitude, radiusKm });

// Silent — the subscription status is reconciled passively (app start, sign-in,
// foreground, and after an IAP verify write settles). A network blip must never
// toast on top of the paywall; callers keep the persisted store on failure.
// Returns null when there is no session (anonymous/signed-out) — the caller must
// treat that as "unknown", NOT as "not subscribed".
export const getSubscriptionStatus = async () => {
  const token = await getIdToken();
  if (!token) return null;
  const res = await fetch(`${BASE_URL}/subscription/status`, {
    headers: { Authorization: token },
  });
  if (!res.ok) {
    const err = new Error(`subscription status fetch failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
};
export const verifyAppleTransaction = (transactionId) =>
  apiPost('/subscription/apple-verify', { transactionId });
export const restoreApplePurchases = (transactionId) =>
  apiPost('/subscription/restore', { transactionId });

export const deleteAccount = () => apiDelete('/user/account');

export default api;
